import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { useState } from "react";
import { FaCopy, FaDiscord, FaInfoCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { PURCHASE_CUSTOM_TIP } from "../../../queries/mutation/cb/cb_mutation";
import { GET_USER_DATA } from "../../../queries/mutation/login_query";
import { GET_CUSTOM_TIP_MENU } from "../../../queries/mutation/rein-tips/queries";
import {
  GET_MY_SUBSCRIPTIONS,
  SUBSCRIBE_TO_MENU_ITEM,
} from "../../../queries/mutation/rein-tips/subscriptionQueries";
import { GET_USER_BROADCASTER_CONFIG } from "../../../queries/mutation/userBroadcasterConfig";
import AuthUser from "../../../types/Auth";
import { isSubscribableType } from "../../../util/subscriptionEligibility";
import { supportsPrivateRedeem } from "../../../util/privateRedeem";
import { useProtectedMenuImages } from "../../../util/useProtectedMenuImages";
import LoadingPage from "../../LoadingPage";

// Tag configuration
const TAG_CONFIG = {
  media_included: {
    icon: "💜",
    label: "Media Included",
    description: "A pic or video will be sent (length/format may vary)",
  },
  fun_support: {
    icon: "🩷",
    label: "Fun & Support",
    description:
      "Fun tip to engage or show support. Media may be sent sometimes, but isn’t guaranteed",
  },
  photo: {
    icon: "📷",
    label: "Photo",
    description: "A Picture will be sent",
  },
  video: {
    icon: "🎥",
    label: "Video",
    description:
      "A video will be sent (usually a short clip, occasional full videos)",
  },
  private_vip: {
    icon: "🔓",
    label: "Private/VIP",
    description: "Media sent via DM or VIP channels",
  },
  public: {
    icon: "🌟",
    label: "Public",
    description: "Media posted in the server",
  },
} as const;

type MenuItem = {
  name: string;
  description: string;
  cost: number;
  friendlyName: string;
  type: string;
  command: string;
  title: string;
  footer: string;
  image: string;
  tipNote: string;
  color: string;
  isActive: boolean;
  isPrivate: boolean;
  isSubscribable?: boolean;
  subscriptionWeeklyPrice?: number | null;
  subscriptionMonthlyPrice?: number | null;
  // What subscribing charges, from the server, so it can't drift from the
  // real charge.
  firstWeeklyCharge?: number | null;
  firstMonthlyCharge?: number | null;
  tags?: string[];
};

// One cadence's price in the subscribe dialog: what's charged now, then
// what each renewal costs when that's less.
const CadencePrice = ({
  first,
  renewal,
  per,
}: {
  first: number;
  renewal: number;
  per: "week" | "month";
}) =>
  first > renewal ? (
    <span className="text-right leading-tight">
      <span className="block font-bold text-purple-400">
        {first.toLocaleString()} CC today
      </span>
      <span className="block text-xs text-base-content/70">
        then {renewal.toLocaleString()} CC / {per}
      </span>
    </span>
  ) : (
    <span className="font-bold text-purple-400">
      {renewal.toLocaleString()} CC / {per}
    </span>
  );

const CustomTipMenu = () => {
  const navigate = useNavigate();
  const [isCopied, setIsCopied] = useState(false);
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [subscribeItem, setSubscribeItem] = useState<MenuItem | null>(null);
  const [subscribeCadence, setSubscribeCadence] = useState<
    "weekly" | "monthly"
  >("weekly");
  // Only meaningful for cadence "weekly" - monthly has no day/time picker.
  const [subscribeDayOfWeek, setSubscribeDayOfWeek] = useState(1); // Monday
  const [subscribeTimeOfDay, setSubscribeTimeOfDay] = useState("10:00");

  const handleError = (error: ApolloError) => {
    toast.error(error.message);
  };
  const { data, loading } = useQuery(GET_CUSTOM_TIP_MENU);
  const [purchaseMutation, { loading: purchaseLoading }] = useMutation(
    PURCHASE_CUSTOM_TIP,
    {
      onError: handleError,
    },
  );

  const { data: subscriptionsData } = useQuery(GET_MY_SUBSCRIPTIONS);
  const [subscribeMutation, { loading: subscribeLoading }] = useMutation(
    SUBSCRIBE_TO_MENU_ITEM,
    {
      onError: handleError,
      refetchQueries: [{ query: GET_MY_SUBSCRIPTIONS }],
    },
  );

  const {
    data: userData,
    loading: userLoading,
    refetch,
  } = useQuery(GET_USER_DATA, { fetchPolicy: "cache-and-network" });

  const user: AuthUser = userData?.getUserData;
  const imageUrls = useProtectedMenuImages(data?.getCustomTipMenu, loading);

  // Fetch user broadcaster config
  const { data: configData } = useQuery(GET_USER_BROADCASTER_CONFIG, {
    variables: { userId: user?.id },
    skip: !user?.id,
  });

  if (loading || !data?.getCustomTipMenu || userLoading || !user) {
    return <LoadingPage />;
  }
  // Fetch protected images using custom hook
  const menu: MenuItem[] = data?.getCustomTipMenu ?? [];
  const showConfirmationModal = (menuItem: MenuItem) => {
    setSelectedItem(menuItem);
    (
      document.getElementById("confirmation_modal") as HTMLDialogElement
    )?.showModal();
  };

  const closeModal = () => {
    (
      document.getElementById("confirmation_modal") as HTMLDialogElement
    )?.close();
  };

  const PurchaseTip = async () => {
    if (!selectedItem) {
      toast.error("No Item selected");
      return;
    }
    const { type: menuItemType } = selectedItem;
    const result = await purchaseMutation({
      variables: { menuItemType },
    });
    await refetch();
    if (result?.data?.purchaseCustomTip) {
      toast.success("Crimcoins redeemed!");
    }

    closeModal();
  };

  const copyCommand = async (command: string) => {
    await navigator.clipboard.writeText(command);
    setIsCopied(true);
    toast.success("Command Copied");
  };

  const showCommandModal = () => {
    (
      document.getElementById("tip_menu_commands") as HTMLDialogElement
    )?.showModal();
  };

  const activeSubscriptionsByType: Record<string, any> = {};
  for (const sub of subscriptionsData?.getMySubscriptions ?? []) {
    if (sub.status === "active") {
      activeSubscriptionsByType[sub.itemType] = sub;
    }
  }

  const showSubscribeModal = (item: MenuItem) => {
    setSubscribeItem(item);
    setSubscribeCadence(item.subscriptionWeeklyPrice ? "weekly" : "monthly");
    // Reset the day/time picker too, not just the item/cadence - otherwise a
    // pick made for one item (then closed without confirming) silently
    // carries over as the default for the next item opened.
    setSubscribeDayOfWeek(1); // Monday
    setSubscribeTimeOfDay("10:00");
    (
      document.getElementById("subscribe_modal") as HTMLDialogElement
    )?.showModal();
  };

  const closeSubscribeModal = () => {
    (
      document.getElementById("subscribe_modal") as HTMLDialogElement
    )?.close();
  };

  const confirmSubscribe = async () => {
    if (!subscribeItem) return;
    const result = await subscribeMutation({
      variables: {
        itemType: subscribeItem.type,
        cadence: subscribeCadence,
        ...(subscribeCadence === "weekly"
          ? { dayOfWeek: subscribeDayOfWeek, timeOfDay: subscribeTimeOfDay }
          : {}),
      },
    });
    // The first cycle is charged right away, so the balance has moved.
    await refetch();
    if (result?.data?.subscribeToMenuItem) {
      toast.success(
        `Subscribed! Your first ${cycle} is paid - it renews automatically from here.`,
      );
      closeSubscribeModal();
    }
  };

  const WEEKDAY_LABELS = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  const subscribePriceFor = (item: MenuItem, cadence: "weekly" | "monthly") =>
    cadence === "weekly"
      ? item.subscriptionWeeklyPrice
      : item.subscriptionMonthlyPrice;

  // What subscribing charges now; renewals charge subscribePriceFor.
  const firstChargeFor = (item: MenuItem, cadence: "weekly" | "monthly") =>
    (cadence === "weekly" ? item.firstWeeklyCharge : item.firstMonthlyCharge) ??
    subscribePriceFor(item, cadence) ??
    0;
  const cycle = subscribeCadence === "weekly" ? "week" : "month";

  // Filter to show only active items
  const activeMenu = menu.filter((item) => item.isActive);

  const discordTokens = user?.discordTokens ?? 0;

  // Check if user has unlimited headpats permission
  const config = configData?.getUserBroadcasterConfig;
  const hasUnlimitedHeadpats = config?.permissions?.unlimitedHeadpats ?? false;

  // Helper function to check if an item is free for this user
  const isItemFree = (item: MenuItem) => {
    return item.type === "headpats" && hasUnlimitedHeadpats;
  };

  // Helper function to get the effective cost (0 if free)
  const getEffectiveCost = (item: MenuItem) => {
    return isItemFree(item) ? 0 : item.cost;
  };
  return (
    <div className="min-h-screen bg-base-200 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-base-content mb-2">
            Crimson Kitten's Offline Tip Menu
          </h1>
          <p className="text-base-content/70">
            Redeem your Crim Coins for exclusive offline tips! 💜
          </p>

          {/* Balance & Actions Bar */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Balance */}
            <div className="bg-purple-900 border border-purple-700 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-purple-100 font-semibold">
                  Available Crim Coins:
                </span>
                <span
                  className={`text-2xl font-bold ${
                    discordTokens === 0 ? "text-red-400" : "text-green-400"
                  }`}
                >
                  {discordTokens.toLocaleString()} CC
                </span>
              </div>
              <button
                disabled={true}
                className="mt-2 w-full py-2 px-4 bg-base-300 text-base-content font-semibold rounded cursor-not-allowed opacity-50"
              >
                Buy Crim Coins (coming soon)
              </button>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2">
              <button
                onClick={showCommandModal}
                className="flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-base-content font-semibold py-3 px-4 rounded transition-colors duration-200"
              >
                <FaDiscord size={24} />
                View Discord Commands
              </button>
              <button
                onClick={() => navigate("/custom-tip-history")}
                className="bg-blue-600 hover:bg-blue-700 text-base-content font-semibold py-2 px-4 rounded transition-colors duration-200"
              >
                View Offline Tip History
              </button>
            </div>
          </div>

          {/* Info Banner */}
          <div className="mt-4 bg-blue-900 border border-blue-700 rounded-lg p-3 flex items-center gap-2">
            <FaInfoCircle className="text-blue-300 flex-shrink-0" />
            <p className="text-blue-100 text-sm">
              All offline tips will notify Crim on her Discord Server
            </p>
          </div>

          {/* Tag Legend */}
          <div className="mt-4 bg-base-100 border border-base-300 rounded-lg p-4">
            <h3 className="text-base-content font-semibold mb-3 flex items-center gap-2">
              <span>🏷️</span>
              Tag Guide
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(TAG_CONFIG).map(([key, config]) => (
                <div
                  key={key}
                  className="flex items-start gap-2 bg-base-200/50 rounded p-2"
                >
                  <span className="text-xl flex-shrink-0">{config.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-purple-300 text-sm font-medium">
                      {config.label}
                    </div>
                    <div className="text-base-content/70 text-xs">
                      {config.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Menu Items Grid */}
        {activeMenu.length === 0 ? (
          <div className="bg-yellow-900 border border-yellow-700 rounded-lg p-6">
            <p className="text-yellow-100 text-lg text-center">
              No menu items available at the moment. Check back soon! 💜
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activeMenu.map((item, index) => {
              const effectiveCost = getEffectiveCost(item);
              const isFree = isItemFree(item);
              const canAfford = discordTokens >= effectiveCost;

              return (
                <div
                  key={`${item.name}-${index}`}
                  className={`bg-base-100 rounded-lg border overflow-hidden transition-all duration-300 shadow-md ${
                    canAfford
                      ? "border-base-300 hover:border-purple-500 hover:shadow-xl hover:shadow-purple-500/30 cursor-pointer"
                      : "border-base-300 opacity-75"
                  } ${isFree ? "ring-2 ring-green-500" : ""}`}
                  onClick={() => canAfford && showConfirmationModal(item)}
                >
                  {/* Image/Icon Section - falls back to the gradient tile when
                      the item has no loadable image */}
                  {imageUrls[item.type] ? (
                    <div className="h-48 bg-base-200 overflow-hidden relative">
                      <img
                        src={imageUrls[item.type]}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div
                      className="h-48 flex items-center justify-center relative"
                      style={{
                        background: `linear-gradient(135deg, ${
                          item.color === "Red"
                            ? "#dc2626, #991b1b"
                            : item.color === "Green"
                              ? "#16a34a, #15803d"
                              : item.color === "Purple"
                                ? "#9333ea, #7e22ce"
                                : item.color === "LuminousVividPink"
                                  ? "#ec4899, #db2777"
                                  : item.color === "Blue"
                                    ? "#2563eb, #1e40af"
                                    : item.color === "White"
                                      ? "#f3f4f6, #d1d5db"
                                      : "#6b7280, #4b5563"
                        })`,
                      }}
                    >
                      <div className="text-6xl">
                        {item.type === "deny"
                          ? "❌"
                          : item.type === "undeny"
                            ? "✅"
                            : item.type === "edge"
                              ? "💦"
                              : item.type === "headpats"
                                ? "💜"
                                : item.type === "spanks"
                                  ? "👋"
                                  : item.type === "hydrate"
                                    ? "💧"
                                    : item.type === "love"
                                      ? "💖"
                                      : item.type === "crim_nude"
                                        ? "📸"
                                        : item.type === "join_vip"
                                          ? "👑"
                                          : item.type === "paddle"
                                            ? "🏓"
                                            : item.type === "instacum"
                                              ? "💥"
                                              : item.type ===
                                                  "buy_panties_socks"
                                                ? "👙"
                                                : item.type === "spa_day"
                                                  ? "💆"
                                                  : item.type === "drink"
                                                    ? "🍹"
                                                    : item.type === "pet_pic"
                                                      ? "🐱"
                                                      : item.type ===
                                                          "horny_jail"
                                                        ? "🔒"
                                                        : item.type ===
                                                            "fingering"
                                                          ? "👆"
                                                          : item.type ===
                                                              "lush_control"
                                                            ? "🎮"
                                                            : item.type ===
                                                                "hush_control"
                                                              ? "🎮"
                                                              : item.type ===
                                                                  "body_writting"
                                                                ? "✍️"
                                                                : "✨"}
                      </div>
                    </div>
                  )}

                  {/* Content Section */}
                  <div className="p-6">
                    {/* Free Badge for Unlimited Headpats */}
                    {isFree && (
                      <div className="mb-3 bg-green-900 border border-green-700 rounded-lg p-2">
                        <div className="text-green-300 text-center font-bold text-sm flex items-center justify-center gap-2">
                          <span>✨</span>
                          <span>FREE - Unlimited Access!</span>
                          <span>✨</span>
                        </div>
                      </div>
                    )}

                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-base-content mb-1">
                          {item.friendlyName || item.name}
                        </h3>
                        {/* Status Badge */}
                        <div className="">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              isFree
                                ? "bg-green-900 text-green-300 border border-green-700"
                                : canAfford
                                  ? "bg-green-900 text-green-300 border border-green-700"
                                  : "bg-red-900 text-red-300 border border-red-700"
                            }`}
                          >
                            {canAfford ? "✓ Available" : "✗ Can't Afford"}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div
                          className={`text-2xl font-bold ${
                            isFree
                              ? "text-green-400 line-through"
                              : canAfford
                                ? "text-purple-400"
                                : "text-red-400"
                          }`}
                        >
                          {!isFree ? item.cost.toLocaleString() : ""}
                        </div>
                        {isFree && (
                          <div className="text-2xl font-bold text-green-400">
                            FREE
                          </div>
                        )}
                        <div className="text-xs text-base-content/50">
                          {isFree ? "" : "Crimcoins"}
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    {item.description && (
                      <p className="text-base-content/80 text-sm mb-4">
                        {item.description}
                      </p>
                    )}

                    {/* Tags */}
                    {item.tags && item.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {item.tags.map((tag) => {
                          const tagKey = tag as keyof typeof TAG_CONFIG;
                          const tagConfig = TAG_CONFIG[tagKey];
                          if (!tagConfig) return null;

                          return (
                            <span
                              key={tag}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-purple-500/20 text-purple-300 rounded-full text-xs"
                              title={tagConfig.description}
                            >
                              <span>{tagConfig.icon}</span>
                              <span>{tagConfig.label}</span>
                            </span>
                          );
                        })}
                      </div>
                    )}

                    {/* Redeem Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        showConfirmationModal(item);
                      }}
                      disabled={!canAfford}
                      className={`w-full font-semibold py-2 px-4 rounded transition-colors duration-200 ${
                        isFree
                          ? "bg-green-600 hover:bg-green-700 text-base-content"
                          : canAfford
                            ? "bg-purple-600 hover:bg-purple-700 text-base-content"
                            : "bg-red-900 text-red-300 cursor-not-allowed"
                      }`}
                    >
                      {isFree
                        ? "✨ Redeem FREE"
                        : canAfford
                          ? "💜 Redeem"
                          : "💰 Insufficient CC"}
                    </button>

                    {/* Subscribe */}
                    {item.isSubscribable &&
                      isSubscribableType(item.type) &&
                      (item.subscriptionWeeklyPrice ||
                        item.subscriptionMonthlyPrice) &&
                      (activeSubscriptionsByType[item.type] ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate("/my-subscriptions");
                          }}
                          className="w-full mt-2 font-semibold py-2 px-4 rounded transition-colors duration-200 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 border border-indigo-700"
                        >
                          🔁 Subscribed - Manage
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            showSubscribeModal(item);
                          }}
                          className="w-full mt-2 font-semibold py-2 px-4 rounded transition-colors duration-200 bg-indigo-600 hover:bg-indigo-700 text-base-content"
                        >
                          🔁 Subscribe
                        </button>
                      ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <dialog
        id="confirmation_modal"
        className="modal modal-bottom sm:modal-middle"
      >
        <div className="modal-box bg-base-100 border border-base-300">
          <h3 className="font-bold text-lg text-center text-base-content">
            Confirm Redemption
          </h3>
          <div className="divider divider-purple m-2"></div>

          {selectedItem && isItemFree(selectedItem) ? (
            <div className="space-y-4">
              <div className="bg-green-900 border border-green-700 rounded-lg p-4 text-center">
                <div className="text-green-300 text-xl font-bold mb-2">
                  ✨ FREE Redemption ✨
                </div>
                <p className="text-green-200 text-sm">
                  You have unlimited access to{" "}
                  <span className="font-bold">
                    {selectedItem?.friendlyName}
                  </span>
                </p>
              </div>
              <p className="text-center text-base-content/80">
                Redeem{" "}
                <span className="text-green-400 font-bold">
                  {selectedItem?.friendlyName}
                </span>{" "}
                at no cost!
              </p>
            </div>
          ) : (
            <>
              <p className="text-center text-base-content/80 mb-4">
                Are you sure you want to exchange{" "}
                <span className="text-purple-400 font-bold">
                  {selectedItem?.cost} CC
                </span>{" "}
                for{" "}
                <span className="text-purple-400 font-bold">
                  {selectedItem?.friendlyName}
                </span>
                ?
              </p>
              <div className="text-center mt-4 bg-base-200 border border-base-300 rounded p-3">
                <div className="text-base-content/70 text-sm">
                  Available Balance:
                </div>
                <div className="text-purple-400 text-2xl font-bold">
                  {discordTokens.toLocaleString()} CC
                </div>
              </div>

              {/* Purely informational - whether a redeem is private is
                  decided by the broadcaster's own item setting, never by the
                  tipper, so this is a badge, not a control. */}
              {supportsPrivateRedeem(selectedItem?.type) &&
                selectedItem?.isPrivate && (
                  <div className="flex items-center justify-between bg-base-200 border border-base-300 rounded px-4 py-3 mt-3">
                    <div>
                      <label className="block text-sm font-semibold text-base-content">
                        🔒 Private Redeem
                      </label>
                      <p className="text-xs text-base-content/50 mt-1">
                        This item is always sent privately by the broadcaster
                      </p>
                    </div>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-900 text-blue-300 border border-blue-700">
                      Always private
                    </span>
                  </div>
                )}
            </>
          )}

          <div className="modal-action justify-center">
            {selectedItem &&
            (isItemFree(selectedItem) ||
              discordTokens >= selectedItem?.cost) ? (
              <button
                className={`btn ${
                  isItemFree(selectedItem)
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-purple-600 hover:bg-purple-700"
                } text-base-content border-0`}
                onClick={PurchaseTip}
                disabled={purchaseLoading}
              >
                {purchaseLoading ? (
                  <div className="loading loading-spinner" />
                ) : isItemFree(selectedItem) ? (
                  "✨ Confirm FREE Tip"
                ) : (
                  "💜 Confirm Tip"
                )}
              </button>
            ) : (
              <button
                disabled={true}
                className="btn bg-purple-600 text-base-content border-0 opacity-50 cursor-not-allowed"
              >
                Buy Crim Coins (coming soon)
              </button>
            )}

            <button
              className="btn bg-red-600 hover:bg-red-700 text-base-content border-0"
              onClick={closeModal}
            >
              Cancel
            </button>
          </div>
        </div>
      </dialog>

      {/* Discord Commands Modal */}
      <dialog
        id="tip_menu_commands"
        className="modal modal-bottom sm:modal-middle"
      >
        <div className="modal-box bg-base-100 border border-base-300 max-w-3xl">
          <h3 className="font-bold text-lg text-purple-400 flex items-center gap-2">
            <FaDiscord size={24} />
            Discord Commands
          </h3>
          <div className="divider divider-purple m-2"></div>
          {isCopied && (
            <div className="bg-green-900 border border-green-700 text-green-300 text-sm text-center p-2 rounded mb-4">
              ✓ Command copied to clipboard
            </div>
          )}
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="table w-full">
              <thead className="sticky top-0 bg-base-100">
                <tr className="border-b border-base-300">
                  <th className="text-purple-400">Menu Item</th>
                  <th className="text-purple-400">Discord Command</th>
                  <th className="text-purple-400">Copy</th>
                </tr>
              </thead>
              <tbody>
                {/* An item with no command is redeemed from this page only. */}
                {menu
                  .filter((t) => t.isActive && t.command)
                  .map((t) => (
                    <tr
                      key={t.name}
                      className="hover:bg-base-300 cursor-pointer border-b border-base-300"
                      onClick={() => copyCommand(t.command)}
                    >
                      <td className="text-purple-300 font-semibold">
                        {t.friendlyName}
                      </td>
                      <td className="text-base-content/80">
                        <code className="bg-base-200 px-2 py-1 rounded text-sm">
                          {t.command}
                        </code>
                      </td>
                      <td>
                        <button className="btn btn-sm bg-purple-600 hover:bg-purple-700 text-base-content border-0">
                          <FaCopy />
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div className="modal-action">
            <form method="dialog">
              <button className="btn bg-red-600 hover:bg-red-700 text-base-content border-0">
                Close
              </button>
            </form>
          </div>
        </div>
      </dialog>

      {/* Subscribe Modal */}
      <dialog id="subscribe_modal" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box bg-base-100 border border-base-300">
          <h3 className="font-bold text-lg text-center text-base-content">
            🔁 Subscribe to {subscribeItem?.friendlyName || subscribeItem?.name}
          </h3>
          <div className="divider divider-purple m-2"></div>
          <p className="text-center text-base-content/80 text-sm mb-4">
            {subscribeItem &&
            firstChargeFor(subscribeItem, subscribeCadence) >
              (subscribePriceFor(subscribeItem, subscribeCadence) ?? 0)
              ? `The first ${cycle} is charged now at the regular price. After that it redeems automatically every ${cycle} at the lower subscription price.`
              : `You're charged for the first ${cycle} now, then it redeems automatically every ${cycle}.`}{" "}
            You can cancel any time from your subscriptions page.
          </p>

          <div className="space-y-2">
            {subscribeItem?.subscriptionWeeklyPrice ? (
              <label
                className={`flex items-center justify-between rounded border px-4 py-3 cursor-pointer ${
                  subscribeCadence === "weekly"
                    ? "border-indigo-500 bg-indigo-900/40"
                    : "border-base-300 bg-base-200"
                }`}
              >
                <span className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="cadence"
                    checked={subscribeCadence === "weekly"}
                    onChange={() => setSubscribeCadence("weekly")}
                  />
                  Weekly
                </span>
                <CadencePrice
                  first={firstChargeFor(subscribeItem, "weekly")}
                  renewal={subscribeItem.subscriptionWeeklyPrice}
                  per="week"
                />
              </label>
            ) : null}
            {subscribeItem?.subscriptionMonthlyPrice ? (
              <label
                className={`flex items-center justify-between rounded border px-4 py-3 cursor-pointer ${
                  subscribeCadence === "monthly"
                    ? "border-indigo-500 bg-indigo-900/40"
                    : "border-base-300 bg-base-200"
                }`}
              >
                <span className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="cadence"
                    checked={subscribeCadence === "monthly"}
                    onChange={() => setSubscribeCadence("monthly")}
                  />
                  Monthly
                </span>
                <CadencePrice
                  first={firstChargeFor(subscribeItem, "monthly")}
                  renewal={subscribeItem.subscriptionMonthlyPrice}
                  per="month"
                />
              </label>
            ) : null}
          </div>

          {subscribeCadence === "weekly" && (
            <div className="mt-3 bg-base-200 border border-base-300 rounded px-4 py-3">
              <label className="block text-sm font-semibold text-base-content mb-2">
                Which day and time should this renew?
              </label>
              <div className="grid grid-cols-2 gap-3">
                <select
                  value={subscribeDayOfWeek}
                  onChange={(e) =>
                    setSubscribeDayOfWeek(Number(e.target.value))
                  }
                  className="bg-base-100 border border-base-300 rounded px-3 py-2 text-base-content focus:outline-none focus:border-indigo-500"
                >
                  {WEEKDAY_LABELS.map((label, index) => (
                    <option key={label} value={index}>
                      {label}
                    </option>
                  ))}
                </select>
                <input
                  type="time"
                  value={subscribeTimeOfDay}
                  onChange={(e) => setSubscribeTimeOfDay(e.target.value)}
                  className="bg-base-100 border border-base-300 rounded px-3 py-2 text-base-content focus:outline-none focus:border-indigo-500"
                />
              </div>
              <p className="text-xs text-base-content/50 mt-2">
                Time is Crim's own clock (Central), not necessarily yours.
              </p>
            </div>
          )}

          {subscribeItem &&
            subscribePriceFor(subscribeItem, subscribeCadence) != null &&
            discordTokens < firstChargeFor(subscribeItem, subscribeCadence) && (
              <p className="text-warning text-xs mt-3 text-center">
                ⚠️ You need{" "}
                {firstChargeFor(subscribeItem, subscribeCadence) - discordTokens}{" "}
                more crimcoins to subscribe.
              </p>
            )}

          <div className="modal-action justify-center">
            <button
              className="btn bg-indigo-600 hover:bg-indigo-700 text-base-content border-0"
              onClick={confirmSubscribe}
              disabled={
                subscribeLoading ||
                !subscribeItem ||
                discordTokens < firstChargeFor(subscribeItem, subscribeCadence)
              }
            >
              {subscribeLoading ? (
                <div className="loading loading-spinner" />
              ) : (
                "Confirm Subscription"
              )}
            </button>
            <button
              className="btn bg-red-600 hover:bg-red-700 text-base-content border-0"
              onClick={closeSubscribeModal}
            >
              Cancel
            </button>
          </div>
        </div>
      </dialog>
    </div>
  );
};

export default CustomTipMenu;
